<script setup lang="ts">
    import { stripBlockTags } from '@/composables/helpers/html';
    import { storeToRefs } from 'pinia';
    import { useWindowStore } from '@/stores/window';

    interface SlideButton {
        label?: string;
        title?: string;
        url?: string;
    }

    interface Slide {
        headline?: string;
        copy?: string;
        button?: SlideButton;
    }

    interface VideoInput {
        video?: string;
        poster?: string;
    }

    const props = withDefaults(
        defineProps<{
            slides?: Slide[];
            video?: VideoInput;
            videoID?: string;
        }>(),
        {}
    );
    const ellipseTopLeft = '/img/2026/sponsor-video/sponsor-video-desktop-top-left-ellipse.png';
    const ellipseBottomLeft = '/img/2026/sponsor-video/sponsor-video-desktop-bottom-left-ellipse.png';
    const ellipseMiddleRight = '/img/2026/sponsor-video/sponsor-video-desktop-middle-right-ellipse.png';
    const ellipseBottomRight = '/img/2026/sponsor-video/sponsor-video-desktop-bottom-right-ellipse.png';
    const heroVideoMp4 = computed(() => props.video?.video || '/img/2026/hero-video/Inbound26Tease_120125-sm.mp4');
    const heroVideoPoster = computed(() => props.video?.poster || '/img/2026/hero-video/Inbound26Tease_120125-sm-poster.jpg');

    const { scrollY, innerHeight } = storeToRefs(useWindowStore());

    const progress = ref(0);
    const videoPlaying = ref(false);
    const videoStarted = ref(false);
    const videoRef = ref(null);
    const rootBox = ref(null);
    const listenToScroll = ref(true);
    let resumeTimer = null;

    // Overlay fades in during 50-100% of progress
    const overlayOpacity = computed(() => {
        const p = progress.value;
        if (p < 50) return 0;
        return Math.min(1, (p - 50) / 30) * 0.85;
    });

    // Text slides up and fades in during 60-90% of progress
    const textOpacity = computed(() => {
        const p = progress.value;
        if (p < 60) return 0;
        return Math.min(1, (p - 60) / 20);
    });

    const textTranslateY = computed(() => {
        const p = progress.value;
        if (p < 60) return 80;
        if (p >= 80) return 0;
        return 80 * (1 - (p - 60) / 20);
    });

    // Play/pause control stays pinned for accessibility — always reachable.
    // Left-anchored to keep clear of the bottom-right Customer Agent chat widget.
    const pauseBtnLeft = computed(() => 4);
    const pauseBtnBottom = computed(() => 4);

    const defaultSlides = [
        {
            headline: 'Partner with UNBOUND',
            copy: 'From turnkey packages, custom solutions, and breakthrough thought leadership, explore sponsorship opportunities that will expand your reach and connect you with our community of growth-minded leaders.',
            button: { label: 'Become a Sponsor', url: '/sponsors' },
        },
    ];

    const activeSlide = computed(() => {
        if (props.slides?.length) return props.slides[0];
        return defaultSlides[0];
    });

    const playVideo = () => {
        const video = videoRef.value;
        if (!video) return;
        const promise = video.play();
        if (promise) {
            promise
                .then(() => {
                    videoPlaying.value = true;
                })
                .catch(() => {
                    videoPlaying.value = false;
                });
        }
    };

    const toggleVideo = () => {
        const video = videoRef.value;
        if (!video) return;
        if (video.paused) {
            playVideo();
        } else {
            video.pause();
            videoPlaying.value = false;
        }
    };

    const handleScroll = () => {
        if (!listenToScroll.value) return;
        const bounds = rootBox.value?.getBoundingClientRect();
        if (!bounds) return;

        // Mask scale starts when the block is fully in view
        const isMobile = innerHeight.value < 768;
        const triggerPoint = 0;
        const scrollPast = Math.max(0, triggerPoint - bounds.top);
        const runway = innerHeight.value * (isMobile ? 1.5 : 3);
        progress.value = Math.min(100, (scrollPast / runway) * 100);
    };

    watch(scrollY, handleScroll);

    onMounted(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const observer = new IntersectionObserver(
            ([entry]) => {
                listenToScroll.value = entry.isIntersecting;
            },
            { root: null, rootMargin: '0px', threshold: 0.001 }
        );
        if (rootBox.value) observer.observe(rootBox.value);

        if (prefersReducedMotion) progress.value = 100;

        // Pause video when off-screen, auto-play when entering viewport
        const visibilityObs = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) {
                clearTimeout(resumeTimer);
                videoRef.value?.pause();
                videoPlaying.value = false;
            } else {
                videoStarted.value = true;
                resumeTimer = setTimeout(() => {
                    playVideo();
                }, 1000);
            }
        });
        if (rootBox.value) visibilityObs.observe(rootBox.value);

        handleScroll();
    });

    onUnmounted(() => {
        clearTimeout(resumeTimer);
    });
</script>

<template>
    <div ref="rootBox" class="sponsor-video-runway" :style="`--progress: ${progress}`">
        <section class="block-sponsor-video sticky overflow-hidden isolate">
            <!-- Decorative ellipses (desktop only) -->
            <div aria-hidden="true" class="absolute inset-0 pointer-events-none overflow-hidden">
                <img :src="ellipseTopLeft" loading="lazy" decoding="async" alt="" class="hidden lg:display-block absolute top-0 left-0" />
                <img :src="ellipseBottomLeft" loading="lazy" decoding="async" alt="" class="hidden lg:display-block absolute bottom-0 left-0" />
                <img :src="ellipseMiddleRight" loading="lazy" decoding="async" alt="" class="hidden lg:display-block absolute bottom-[130px] right-[240px]" />
                <img :src="ellipseBottomRight" loading="lazy" decoding="async" alt="" class="hidden lg:display-block absolute bottom-0 right-0" />
            </div>

            <!-- Masked video — rotates as mask scales up -->
            <div class="sponsor-video-mask absolute inset-0">
                <div class="sponsor-video-content absolute inset-[-10%]">
                    <video ref="videoRef" muted loop playsinline :poster="heroVideoPoster" class="absolute inset-0 w-full h-full object-cover">
                        <source :src="heroVideoMp4" type="video/mp4" />
                    </video>
                    <!-- Gradient overlay matching Figma spec -->
                    <div aria-hidden="true" class="absolute inset-0" style="background: linear-gradient(to bottom, rgba(255, 72, 0, 0) 0%, rgba(130, 12, 167, 0.5) 100%)" />
                </div>
            </div>

            <!-- Pause/Play control -->
            <button
                v-if="videoStarted"
                class="group sponsor-video-pause-btn absolute z-[3] w-[31px] h-[31px] rounded-full bg-[var(--magenta)] hover:bg-white border-none cursor-pointer flex items-center justify-center transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                :style="{ bottom: pauseBtnBottom + '%', left: pauseBtnLeft + '%' }"
                :aria-label="videoPlaying ? 'Pause video' : 'Play video'"
                @click="toggleVideo">
                <svg v-if="videoPlaying" aria-hidden="true" width="9" height="11" viewBox="0 0 9 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="3" height="11" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                    <rect x="6" width="3" height="11" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                </svg>
                <svg v-else aria-hidden="true" width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="ml-[2px]">
                    <path d="M10 6L0 12V0L10 6Z" class="fill-white group-hover:fill-(--magenta) transition-[fill] duration-200" />
                </svg>
            </button>

            <!-- Purple gradient overlay — fades in on scroll -->
            <div class="absolute inset-0 z-1 pointer-events-none" :style="{ opacity: overlayOpacity, background: 'linear-gradient(to bottom, #820ca7 0%, #46062b 100%)' }" />

            <!-- Text content — slides up over video -->
            <div
                class="absolute inset-0 z-[2] flex flex-col items-center justify-center px-(--box-padding-lr-m) lg:px-(--box-padding-lr)"
                :style="{ opacity: textOpacity, transform: `translateY(${textTranslateY}px)` }">
                <div class="flex flex-col gap-[31px] items-center max-w-[899px] text-center">
                    <div class="flex flex-col gap-[18px] items-center text-white w-full">
                        <h2
                            class="font-hubspot-serif font-normal text-[calc(36/var(--base-fs)*1rem)] leading-[1.1] tracking-normal md:text-[calc(60/var(--base-fs)*1rem)]"
                            v-html="stripBlockTags(activeSlide.headline)" />
                        <div
                            v-if="activeSlide.copy"
                            class="font-hubspot-sans font-light text-base leading-[26px] tracking-[0.32px] text-white max-w-[669px]"
                            v-html="activeSlide.copy" />
                    </div>
                    <a
                        v-if="activeSlide.button?.label"
                        :href="activeSlide.button.url || '#'"
                        :title="activeSlide.button.title"
                        class="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-[50px] bg-white font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] text-(--magenta) no-underline transition-colors duration-200 hover:bg-(--orange) hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white pointer-events-auto">
                        {{ activeSlide.button.label }}
                    </a>
                </div>
            </div>
        </section>
    </div>
</template>

<style>
    .sponsor-video-runway {
        height: 400svh;
        background: linear-gradient(246.65deg, var(--purple) 0%, var(--purple) 69.58%, rgb(201, 46, 73) 100%);
    }

    .block-sponsor-video {
        top: 0;
        height: 100svh;
        height: 100dvh;
    }

    .sponsor-video-mask {
        --svg-ratio: calc(1272 / 810);
        --viewport-ratio: calc(100vw / 100svh);
        --aspect-scale: max(1, calc(var(--svg-ratio) / var(--viewport-ratio)));
        --mask-rotate: calc(8deg * (1 - var(--progress, 0) / 100));
        --mask-scale: calc(var(--aspect-scale) + (var(--progress, 0) / 100) * 10);
        transform: rotate(var(--mask-rotate));
        transform-origin: center;
    }

    .sponsor-video-content {
        transform: rotate(calc(var(--mask-rotate) * -1));
        transform-origin: center;
        --mask-svg: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1272 810'%3E%3Cpath d='M667.534 274.379C703.184 248.542 751.128 248.542 786.778 274.379L1229.33 596.113C1264.98 621.95 1279.73 667.473 1266.21 709.304L1228.92 810H0C4.31125 805.627 9.03402 801.57 14.1523 797.888L667.534 274.379ZM908.168-.034668C905.812 2.60134 903.168 5.09026 900.231 7.396L246.417 522.488C205.857 554.438 146.634 523.621 149.62 472.273L149.778 471.756L176.217-.000488281L908.168-.034668Z' fill='%23fff'/%3E%3C/svg%3E");
        -webkit-mask-image: var(--mask-svg);
        mask-image: var(--mask-svg);
        --mask-h: calc(var(--mask-scale) * 100%);
        --mask-w: calc(var(--mask-h) * 1271.17 / 810.04);
        -webkit-mask-size: var(--mask-w) var(--mask-h);
        mask-size: var(--mask-w) var(--mask-h);
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
    }

    .sponsor-video-pause-btn {
        animation: sponsor-fade-in 0.4s ease-out both;
    }

    @keyframes sponsor-fade-in {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @media (max-width: 767px) {
        .sponsor-video-runway {
            height: 400svh;
        }

        .sponsor-video-mask {
            --svg-ratio: calc(430 / 935);
            --mask-rotate: calc(5deg * (1 - var(--progress, 0) / 100));
        }

        .sponsor-video-content {
            --mask-svg: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 430 935'%3E%3Cpath fill-rule='evenodd' d='M497.88 176.274C520.507 159.906 550.936 159.906 573.563 176.274L854.448 380.104C877.074 396.472 886.436 425.313 877.854 451.814L692.939 950.281C683.186 979.121 656.268 998.998 625.449 998.998H243.134C212.315 998.998 185.007 979.511 175.644 950.281L57.4378 587.052C48.075 557.822 58.2184 525.863 83.1859 507.936L497.491 175.885L497.88 176.274ZM39.8958-223.554C55.0375-236.351 74.894-241.731 94.1361-238.675L375.974-194.841C414.193-189.123 439.667-153.032 432.626-115.049L382.001 159.47C379.126 176.02 370.118 190.695 356.807 201.11L23.5091 460.719L23.1156 460.984C-7.83585 484.738-52.3802 478.133-74.8708 446.044L-292.023 134.531C-312.326 105.263-307.006 65.5761-279.671 42.8301L39.8958-223.554Z' fill='%23fff'/%3E%3C/svg%3E");
            --mask-w: calc(var(--mask-scale) * 100%);
            --mask-h: calc(var(--mask-w) * 935 / 430);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .sponsor-video-pause-btn {
            animation: none !important;
            opacity: 1 !important;
        }
    }
</style>
